import { Link } from 'react-router-dom';
import { Bike, Store, ShoppingBag, UtensilsCrossed, MapPin, ArrowRight } from 'lucide-react';
import { StoreBadges } from '@/components/landing/StoreBadges';
import { RestaurantMarquee } from '@/components/landing/RestaurantMarquee';
import { HowToOrder } from '@/components/landing/HowToOrder';
import { FAQ } from '@/components/landing/FAQ';
import heroShot from '@/assets/landing/first.png.asset.json';
import { assetUrl } from '@/lib/assetUrl';

const LOGO = '/lovable-uploads/40013902-21dc-443e-b648-06fc550f98e5.png';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="GAMBGO logo" className="h-9 w-9" />
            <span className="text-lg font-extrabold tracking-tight text-landing-ink">GAMBGO</span>
          </div>
          <nav className="flex items-center gap-1 sm:gap-4">
            <Link
              to="/about"
              className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-brand"
            >
              About us
            </Link>
            <a
              href="#get-the-app"
              className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-sm transition-transform hover:-translate-y-0.5"
            >
              Get the app
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="overflow-hidden px-4 pb-4 pt-10 sm:pt-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2">
          <div className="text-center md:text-left">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-landing-surface px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-brand">
              <MapPin className="h-3.5 w-3.5" /> Serving The Gambia
            </span>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-landing-ink sm:text-6xl">
              Delivering
              <br />
              happiness.
            </h1>
            <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-muted-foreground md:mx-0">
              Food, groceries and everyday shopping delivered across The Gambia. Fast, transparent,
              and built for how you order.
            </p>
            <StoreBadges className="mt-8 justify-center md:justify-start" />
            <Link
              to="/home"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
            >
              Order online <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-1/2 -z-10 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-3xl" />
            <img
              src={assetUrl(heroShot.url)}
              alt="The GAMBGO app open on a phone showing nearby restaurants"
              className="mx-auto w-full max-w-[360px] translate-x-4 drop-shadow-2xl sm:max-w-[440px] sm:translate-x-0"
            />
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="px-4 py-12 sm:py-16">
        <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-3">
          {[
            {
              icon: UtensilsCrossed,
              title: 'Food delivery',
              body: 'Your favourite restaurants, delivered hot and fast.',
            },
            {
              icon: ShoppingBag,
              title: 'Marketplace & groceries',
              body: 'Shop local stores for everything from produce to phones.',
            },
            {
              icon: Bike,
              title: 'Live tracking',
              body: 'Follow your rider on the map from kitchen to doorstep.',
            },
          ].map((f) => (
            <div key={f.title} className="rounded-3xl bg-landing-surface p-6">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mb-1.5 text-lg font-bold text-landing-ink">{f.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Transparent pricing */}
      <section className="px-4 py-12 sm:py-20">
        <div className="mx-auto grid max-w-5xl items-center gap-10 rounded-[2rem] bg-landing-surface p-6 sm:p-12 md:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-landing-ink sm:text-4xl">
              No hidden fees. Ever.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
              Delivery fees are shown upfront before you pay, and promos apply right in your cart.
              The price you see is the price you pay.
            </p>
          </div>
          <div className="rounded-3xl bg-background p-6 shadow-xl">
            {[
              ['Subtotal', 'D400'],
              ['Delivery fee', 'D80'],
              ['Promo', '-D50'],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium text-landing-ink">{value}</span>
              </div>
            ))}
            <div className="mt-2 flex items-center justify-between border-t border-border pt-4">
              <span className="font-semibold text-landing-ink">Total</span>
              <span className="text-xl font-bold text-landing-ink">D430</span>
            </div>
            <div className="mt-5 rounded-2xl bg-brand py-3 text-center font-semibold text-brand-foreground">
              Pay D430
            </div>
          </div>
        </div>
      </section>

      {/* How to order */}
      <HowToOrder />

      {/* Restaurants */}
      <section className="bg-landing-surface px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-landing-ink sm:text-4xl">
              Restaurants and stores on GAMBGO
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
              From local favourites to popular spots across the Kombos, discover and order from
              vendors near you.
            </p>
          </div>
          <RestaurantMarquee />
          <p className="mt-8 text-center text-sm text-muted-foreground">
            More vendors joining every week.
          </p>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="px-4 py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-2">
          {[
            {
              icon: Store,
              title: 'Become a vendor',
              body: 'Put your restaurant or store in front of thousands of customers across The Gambia.',
              to: '/vendor-onboarding',
              cta: 'Partner with us',
            },
            {
              icon: Bike,
              title: 'Drive with GAMBGO',
              body: 'Deliver on your own schedule and get paid for every completed order.',
              to: '/driver-application',
              cta: 'Start driving',
            },
          ].map((c) => (
            <div
              key={c.title}
              className="flex flex-col rounded-[2rem] border border-border bg-background p-8 transition-shadow hover:shadow-lg"
            >
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                <c.icon className="h-6 w-6" />
              </div>
              <h3 className="mb-2 text-2xl font-bold text-landing-ink">{c.title}</h3>
              <p className="mb-6 flex-1 leading-relaxed text-muted-foreground">{c.body}</p>
              <Link
                to={c.to}
                className="inline-flex w-fit items-center gap-1.5 rounded-full bg-landing-ink px-5 py-2.5 text-sm font-semibold text-background transition-transform hover:-translate-y-0.5"
              >
                {c.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Download band */}
      <section id="get-the-app" className="px-4 pb-16">
        <div className="mx-auto max-w-6xl rounded-[2rem] bg-brand px-6 py-14 text-center sm:px-12">
          <h2 className="text-3xl font-extrabold tracking-tight text-brand-foreground sm:text-4xl">
            Get GAMBGO on your phone
          </h2>
          <p className="mx-auto mt-3 max-w-md text-brand-foreground/90">
            Download the app and order food, groceries and more in a few taps.
          </p>
          <StoreBadges variant="light" className="mt-8 justify-center" />
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Footer */}
      <footer className="bg-landing-ink px-4 py-12 text-background">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-3">
            <div>
              <div className="mb-4 flex items-center gap-2.5">
                <img src={LOGO} alt="GAMBGO logo" className="h-8 w-8" />
                <span className="text-lg font-bold">GAMBGO</span>
              </div>
              <p className="max-w-xs text-sm text-background/70">
                Delivering happiness across The Gambia. Your one-stop shop for food, groceries and
                more.
              </p>
            </div>
            <div>
              <h3 className="mb-4 text-sm font-semibold">Company</h3>
              <ul className="space-y-2 text-sm text-background/70">
                <li>
                  <Link to="/about" className="transition-colors hover:text-background">
                    About us
                  </Link>
                </li>
                <li>
                  <Link to="/vendor-onboarding" className="transition-colors hover:text-background">
                    Become a vendor
                  </Link>
                </li>
                <li>
                  <a href="tel:+2202554992" className="transition-colors hover:text-background">
                    Contact: +220 255 4992
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-4 text-sm font-semibold">Legal</h3>
              <ul className="space-y-2 text-sm text-background/70">
                <li>
                  <Link to="/privacy" className="transition-colors hover:text-background">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="transition-colors hover:text-background">
                    Terms &amp; Conditions
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/15 pt-6 text-center text-xs text-background/60">
            <p>&copy; 2026 GAMBGO. All rights reserved. Powered by gambgo express.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
